import { useCallback, useEffect, useState, type ReactNode } from 'react';

import { ErroApi } from './api';

/* ── Tema ────────────────────────────────────────────────────────────────── */

export type Tema = 'escuro' | 'claro';

const CHAVE_TEMA = 'lenanzo.tema';

/** Aplicado antes do primeiro render, para não piscar branco ao carregar. */
export function aplicarTemaSalvo(): Tema {
  const salvo = localStorage.getItem(CHAVE_TEMA);
  const tema: Tema = salvo === 'claro' ? 'claro' : 'escuro';
  document.documentElement.dataset['tema'] = tema;
  return tema;
}

export function usarTema(): { tema: Tema; alternar: () => void } {
  const [tema, setTema] = useState<Tema>(() =>
    document.documentElement.dataset['tema'] === 'claro' ? 'claro' : 'escuro',
  );

  const alternar = useCallback(() => {
    setTema((atual) => {
      const novo: Tema = atual === 'escuro' ? 'claro' : 'escuro';
      document.documentElement.dataset['tema'] = novo;
      localStorage.setItem(CHAVE_TEMA, novo);
      return novo;
    });
  }, []);

  return { tema, alternar };
}

export function BotaoTema(): ReactNode {
  const { tema, alternar } = usarTema();
  return (
    <button
      className="icone"
      onClick={alternar}
      title={tema === 'escuro' ? 'Mudar para tema claro' : 'Mudar para tema escuro'}
      aria-label="Alternar tema"
    >
      {tema === 'escuro' ? '☀' : '☾'}
    </button>
  );
}

/* ── Marca ───────────────────────────────────────────────────────────────── */

/**
 * Logotipo da Lenanzo.
 *
 * Marca-palavra em HTML, não imagem: fica nítido em qualquer densidade de tela,
 * acompanha o tema claro/escuro sem um segundo arquivo, e não adiciona uma
 * requisição de rede — o que importa num produto que roda em ambiente fechado.
 *
 * A construção é a do logotipo original: `lenanzo` em peso forte, `tech` em
 * peso leve, sem espaço entre os dois, com inclinação e espaçamento negativo.
 */
export function Logotipo({
  tamanho = 22,
  descritivo = false,
  invertido = false,
}: {
  tamanho?: number;
  /** Mostra "soluções em tecnologia" abaixo. Só em superfície grande. */
  descritivo?: boolean;
  /** Sobre fundo cheio da marca, o logotipo vira branco. */
  invertido?: boolean;
}): ReactNode {
  return (
    <span
      className={`logotipo${invertido ? ' invertido' : ''}`}
      style={{ fontSize: `${tamanho}px` }}
    >
      <span className="marca-palavra">
        <b>lenanzo</b>
        <i>tech</i>
      </span>
      {descritivo && <span className="descritivo">soluções em tecnologia</span>}
    </span>
  );
}

/* ── Peças visuais ───────────────────────────────────────────────────────── */

export function Aviso({
  tipo = 'info',
  flutuante = false,
  children,
}: {
  tipo?: 'info' | 'erro' | 'ok' | 'alerta';
  /**
   * Fixa o aviso na tela, visível de qualquer ponto da rolagem.
   *
   * 🔴 Para confirmação de ação disparada por botão que não está no topo.
   * Renderizar "salvo." no alto de uma página longa é o mesmo que não
   * renderizar: em 2026-09-14 isso custou 18 cliques em Salvar na tela de
   * Configurações, porque a tela não dava sinal nenhum de vida.
   */
  flutuante?: boolean;
  children: ReactNode;
}): ReactNode {
  const classes = ['aviso', tipo === 'info' ? '' : tipo, flutuante ? 'aviso-flutuante' : '']
    .filter((c) => c !== '')
    .join(' ');
  return <div className={classes}>{children}</div>;
}

/**
 * Cartão de número.
 *
 * `nota` é o que transforma contagem em informação: "10" não diz nada, "10 ·
 * fora de suporte" vira tarefa. `destaque` só acende quando o valor é maior
 * que zero — cartão vermelho marcando zero ensina a ignorar vermelho.
 */
/**
 * "Mostrando N de M" — a frase que faltava em cinco lugares.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * 🔴 **Levantado em 17/09/2026 navegando pelo produto: cinco telas cortavam a
 * lista e nenhuma dizia que estava cortando.**
 *
 *   • Relatórios mostrava 3 das 4 versões de Windows — e a escondida era
 *     `win7_ou_anterior: 1`, o pior caso do parque.
 *   • Sentinela: 200 violações de quantas houvesse.
 *   • Perguntar: 100 linhas de resultado.
 *   • Painel inicial: 8 barras num gráfico cujo total contava todas.
 *
 * ⚠️ É o modo de falha desta base com outra roupa. Não dá erro, não quebra
 * teste, e o número que sobra é plausível — ninguém desconfia de uma lista que
 * parece completa. O caso do Windows 7 é o retrato: o corte apagou exatamente
 * o item que o relatório existia para mostrar.
 *
 * Este componente não resolve o corte — cortar continua certo, porque ninguém
 * lê 7 mil linhas. Ele resolve o **silêncio**.
 * ─────────────────────────────────────────────────────────────────────────────
 */
export function Corte({
  mostrando,
  total,
  unidade = 'itens',
  /** O que fazer para ver o resto. Omitido quando não há caminho. */
  comoVerTudo,
}: {
  mostrando: number;
  total: number;
  unidade?: string;
  comoVerTudo?: string;
}): ReactNode {
  // Nada cortado, nada a dizer. Anunciar "10 de 10" é ruído.
  if (total <= mostrando) return null;

  return (
    <p className="corte" role="status">
      Mostrando <strong>{mostrando}</strong> de <strong>{total}</strong> {unidade}.
      {comoVerTudo === undefined ? '' : ` ${comoVerTudo}`}
    </p>
  );
}

export function Cartao({
  rotulo,
  valor,
  nota,
  tom = 'neutro',
  destaque = false,
  titulo,
}: {
  rotulo: string;
  valor: number | string;
  nota?: string;
  tom?: TomEtiqueta;
  destaque?: boolean;
  titulo?: string;
}): ReactNode {
  const acende = destaque && typeof valor === 'number' && valor > 0;
  const classe = acende ? (tom === 'neutro' ? 'alerta' : tom) : '';
  return (
    <div
      className={`cartao${classe === '' ? '' : ` acende-${classe}`}`}
      {...(titulo === undefined ? {} : { title: titulo })}
    >
      <div className="rotulo">{rotulo}</div>
      <div className="valor">{valor}</div>
      {nota !== undefined && <div className="nota">{nota}</div>}
    </div>
  );
}

export function Painel({
  titulo,
  sub,
  children,
}: {
  titulo?: string;
  sub?: string;
  children: ReactNode;
}): ReactNode {
  return (
    <section className="painel">
      {titulo !== undefined && <h2>{titulo}</h2>}
      {sub !== undefined && <p className="sub">{sub}</p>}
      {children}
    </section>
  );
}

export function Campo({
  rotulo,
  ajuda,
  children,
}: {
  rotulo: string;
  /**
   * `| undefined` explícito por causa do `exactOptionalPropertyTypes`.
   *
   * Sem ele, `ajuda={condicao ? 'texto' : undefined}` não compila — e essa é a
   * forma natural de escrever ajuda condicional numa tela. O componente já
   * trata a ausência; o tipo é que precisava dizer a verdade.
   */
  ajuda?: string | undefined;
  children: ReactNode;
}): ReactNode {
  return (
    <label>
      <span className="rotulo">{rotulo}</span>
      {children}
      {ajuda !== undefined && <span className="ajuda">{ajuda}</span>}
    </label>
  );
}

/** Data curta. `null` vira travessão em vez de "Invalid Date". */
export function data(iso: string | null | undefined): string {
  if (iso === null || iso === undefined || iso === '') return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
}

export const pct = (v: number | null): string =>
  v === null ? '—' : `${(v * 100).toFixed(1).replace('.0', '')}%`;

/* ── Semântica do parque ─────────────────────────────────────────────────── */

/** Dias desde a data. `null` quando não há data ou ela é inválida. */
export function diasDesde(iso: string | null | undefined): number | null {
  if (iso === null || iso === undefined || iso === '') return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return Math.floor((Date.now() - d.getTime()) / 86_400_000);
}

/** Acima disto, o inventário da máquina não descreve mais a realidade. */
export const DIAS_INVENTARIO_VELHO = 30;

/**
 * Data com idade ao lado.
 *
 * `24/04/2026` não diz nada a quem está olhando 40 linhas; `24/04/2026 · 105
 * dias` diz que aquele agente parou. A data absoluta continua porque é ela que
 * se cola num chamado.
 */
export function Tempo({
  iso,
  alertarApos = DIAS_INVENTARIO_VELHO,
}: {
  iso: string | null | undefined;
  alertarApos?: number;
}): ReactNode {
  const dias = diasDesde(iso);
  if (dias === null) return <span className="suave">nunca</span>;

  const velho = dias >= alertarApos;
  return (
    <span className={velho ? 'atencao' : undefined}>
      {data(iso)}
      <span className="idade">{dias === 0 ? 'hoje' : dias === 1 ? 'ontem' : `${dias} dias`}</span>
    </span>
  );
}

export type TomEtiqueta = 'neutro' | 'ok' | 'alerta' | 'erro';

export function Etiqueta({
  tom = 'neutro',
  titulo,
  children,
}: {
  tom?: TomEtiqueta;
  titulo?: string;
  children: ReactNode;
}): ReactNode {
  return (
    <span
      className={`etiqueta${tom === 'neutro' ? '' : ` ${tom}`}`}
      {...(titulo === undefined ? {} : { title: titulo })}
    >
      {children}
    </span>
  );
}

export interface SistemaExibido {
  readonly texto: string;
  readonly tom: TomEtiqueta;
  readonly titulo?: string;
}

/**
 * Como mostrar o sistema operacional de um dispositivo.
 *
 * Existe porque a tabela estava inconsistente: Windows ganhava etiqueta, o
 * resto virava texto cru vindo do banco (`Microsoft Windows 10 Pro x64`,
 * `Linux Mint 22.3`), e os cartões diziam `linux` e `macos` em minúsculo.
 *
 * ⚠️ **A versão do Windows vem de `versaoWindows`, nunca de `soNome`.** O
 * Windows 11 reporta `Os_version` começando em `10.0`; só o build distingue, e
 * essa derivação já foi feita na sincronização. Ler o nome aqui reintroduziria
 * o erro que o adapter existe para evitar.
 */
export function sistemaDe(d: {
  soFamilia?: string | null;
  versaoWindows?: string | null;
  soNome?: string | null;
}): SistemaExibido {
  const v = d.versaoWindows;
  if (v === 'win11') return { texto: 'Windows 11', tom: 'ok' };
  if (v === 'win10') {
    return {
      texto: 'Windows 10',
      tom: 'alerta',
      titulo: 'Fora do suporte da Microsoft desde outubro de 2025.',
    };
  }
  if (v === 'win8') return { texto: 'Windows 8', tom: 'erro', titulo: 'Sem suporte.' };
  if (v === 'win7') {
    return { texto: 'Windows 7 ou anterior', tom: 'erro', titulo: 'Sem suporte.' };
  }

  const familia = (d.soFamilia ?? '').toLowerCase();
  if (familia === 'macos') return { texto: 'macOS', tom: 'neutro' };
  if (familia === 'android') return { texto: 'Android', tom: 'neutro' };
  if (familia === 'linux' || familia === 'unix') {
    // A distro quase nunca traz "linux" no nome (openSUSE, Mint, Ubuntu). Mostrar
    // o nome real ajuda mais que a família — mas cortado, porque nome de distro
    // é longo e a coluna é estreita.
    const nome = (d.soNome ?? '').trim();
    return { texto: nome === '' ? 'Linux' : nome.slice(0, 22), tom: 'neutro' };
  }
  if (familia === 'windows') {
    return {
      texto: 'Windows',
      tom: 'alerta',
      titulo: 'Sem build reportado — a versão não pôde ser determinada.',
    };
  }

  const nome = (d.soNome ?? '').trim();
  return { texto: nome === '' ? 'desconhecido' : nome.slice(0, 22), tom: 'neutro' };
}

/* ── Carregamento ────────────────────────────────────────────────────────── */

export interface Carga<T> {
  readonly dados: T | null;
  readonly carregando: boolean;
  readonly erro: string | null;
  readonly recarregar: () => void;
}

/**
 * Busca com estado de carregamento e erro.
 *
 * ⚠️ Sessão vencida é **propagada para cima**, não mostrada como erro do widget.
 * Um 401 em qualquer requisição significa que a tela inteira precisa voltar ao
 * login — mostrar "não autenticado" dentro de um cartão deixaria o usuário
 * olhando para seis mensagens de erro sem entender que basta entrar de novo.
 */
export function useCarga<T>(
  buscar: () => Promise<T>,
  deps: readonly unknown[],
  aoPerderSessao: () => void,
): Carga<T> {
  const [dados, setDados] = useState<T | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [gatilho, setGatilho] = useState(0);

  useEffect(() => {
    let vivo = true;
    setCarregando(true);
    setErro(null);

    buscar()
      .then((d) => {
        if (vivo) setDados(d);
      })
      .catch((e: unknown) => {
        if (!vivo) return;
        if (e instanceof ErroApi && e.precisaLogin) {
          aoPerderSessao();
          return;
        }
        setErro(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        if (vivo) setCarregando(false);
      });

    return () => {
      vivo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, gatilho]);

  const recarregar = useCallback(() => setGatilho((g) => g + 1), []);
  return { dados, carregando, erro, recarregar };
}

/** Envolve uma ação (botão) tratando erro e sessão vencida de forma uniforme. */
export function useAcao(aoPerderSessao: () => void): {
  executando: boolean;
  erro: string | null;
  ok: string | null;
  limpar: () => void;
  rodar: (f: () => Promise<string | null>) => Promise<void>;
  /** Recusa antes de chamar o servidor — validação feita aqui. */
  falhar: (mensagem: string) => void;
} {
  const [executando, setExecutando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);

  /**
   * A confirmação some sozinha; o erro fica.
   *
   * "Cartão atualizado." ficava no topo empurrando a tela para baixo até a
   * próxima ação — e não havia nada a fazer com ela depois de lida. Erro é o
   * oposto: some antes de ser lido é erro perdido, e quem precisa dele às vezes
   * precisa copiar o texto.
   */
  useEffect(() => {
    if (ok === null) return;
    const t = setTimeout(() => setOk(null), 4000);
    return () => clearTimeout(t);
  }, [ok]);

  const rodar = useCallback(
    async (f: () => Promise<string | null>) => {
      setExecutando(true);
      setErro(null);
      setOk(null);
      try {
        setOk(await f());
      } catch (e: unknown) {
        if (e instanceof ErroApi && e.precisaLogin) {
          aoPerderSessao();
          return;
        }
        setErro(e instanceof Error ? e.message : String(e));
      } finally {
        setExecutando(false);
      }
    },
    [aoPerderSessao],
  );

  const limpar = useCallback(() => {
    setErro(null);
    setOk(null);
  }, []);

  /**
   * Recusar sem ir ao servidor.
   *
   * Existe porque validação no cliente não tinha onde aparecer: a tela ou
   * mandava dado inválido e traduzia o 400, ou deixava passar em silêncio.
   */
  const falhar = useCallback((mensagem: string) => {
    setOk(null);
    setErro(mensagem);
  }, []);

  return { executando, erro, ok, limpar, rodar, falhar };
}

/** Estado de carregamento e erro, desenhado uma vez só. */
export function Estado({
  carga,
  children,
}: {
  carga: Carga<unknown>;
  children: ReactNode;
}): ReactNode {
  if (carga.erro !== null) return <Aviso tipo="erro">{carga.erro}</Aviso>;
  if (carga.carregando && carga.dados === null) {
    return <p className="suave">Carregando…</p>;
  }
  return <>{children}</>;
}

/**
 * Avisa quando o navegador está com uma versão anterior à do servidor.
 *
 * 🔴 **Aba velha é o problema invisível mais caro que enfrentamos.** Duas vezes
 * num dia o produto foi dado como quebrado enquanto o navegador rodava código
 * anterior à correção — e nada na tela dizia isso. O erro aparecia; a causa,
 * não. Na instalação do cliente é pior: depois de uma atualização, quem está
 * com a tela aberta abre chamado sobre um defeito que já foi consertado.
 *
 * Compara o nome do arquivo que este código veio (o hash está no nome) com o
 * que o servidor entrega agora. Nomes diferentes = versões diferentes.
 */
export function AvisoDeVersao(): ReactNode {
  const [desatualizado, setDesatualizado] = useState(false);

  useEffect(() => {
    const meu = /assets\/(index-[A-Za-z0-9_-]+\.js)/.exec(import.meta.url)?.[1] ?? null;
    if (meu === null) return; // modo de desenvolvimento do Vite: não há hash.

    let vivo = true;
    const conferir = async (): Promise<void> => {
      try {
        const r = await fetch('/api/versao', { cache: 'no-store' });
        const j = (await r.json()) as { front?: string | null };
        if (vivo && typeof j.front === 'string' && j.front !== meu) setDesatualizado(true);
      } catch {
        // Servidor fora do ar é outro problema, com outro aviso. Aqui, silêncio.
      }
    };

    void conferir();
    // A cada minuto: barato, e pega a atualização que aconteceu enquanto a
    // pessoa estava com a aba aberta.
    const t = setInterval(() => void conferir(), 60_000);
    return () => {
      vivo = false;
      clearInterval(t);
    };
  }, []);

  if (!desatualizado) return null;

  return (
    <div className="aviso-versao">
      <span>
        <strong>Esta tela está desatualizada.</strong> O servidor já tem uma versão mais nova — o
        que você vê pode não refletir as últimas correções.
      </span>
      <button className="primario" onClick={() => window.location.reload()}>
        Recarregar
      </button>
    </div>
  );
}

/**
 * Recarrega enquanto a aba está à vista.
 *
 * 🔴 **Três cuidados que decidem se isto ajuda ou atrapalha:**
 *
 *   1. **Só com a aba visível.** Aba de fundo esquecida por uma semana
 *      consultaria o banco a noite inteira, e a instalação do cliente pode ter
 *      dez pessoas com a tela aberta. Ninguém está lendo — não há o que
 *      atualizar.
 *   2. **Ao voltar o foco, imediatamente.** É o momento em que a pessoa olha,
 *      e é quando um número velho engana.
 *   3. **Não substitui o F5.** A tela recarrega dados, não código — para
 *      versão nova existe o `AvisoDeVersao`.
 */
export function useAtualizacaoPeriodica(recarregar: () => void, segundos = 60): void {
  useEffect(() => {
    const visivel = (): boolean =>
      typeof document === 'undefined' || document.visibilityState === 'visible';

    const t = setInterval(() => {
      if (visivel()) recarregar();
    }, segundos * 1000);

    const aoFocar = (): void => {
      if (visivel()) recarregar();
    };
    window.addEventListener('focus', aoFocar);
    document.addEventListener('visibilitychange', aoFocar);

    return () => {
      clearInterval(t);
      window.removeEventListener('focus', aoFocar);
      document.removeEventListener('visibilitychange', aoFocar);
    };
  }, [recarregar, segundos]);
}
